<template>
  <div class="payment-page">
    <div class="main-container">
      <!-- 头部 -->
      <div class="header">
        <svg width="18" height="18" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" @click="goBack">
          <path d="M778.671 926.323a56.811 56.811 0 1 1-80.33 80.331L243.85 552.165a56.811 56.811 0 0 1 0-80.33l454.49-454.49a56.811 56.811 0 1 1 80.33 80.331L364.348 512 778.67 926.323z"></path>
        </svg>
        <div class="header-title">管理付款页</div>
      </div>

      <!-- 添加按钮 -->
      <div class="add-btn" @click="showAddModal = true">
        <span class="add-icon">+</span>
        <span>添加新的付款页</span>
      </div>

      <!-- 付款页列表 -->
      <div class="payment-list">
        <div class="payment-item" v-for="(item, index) in paymentList" :key="index">
          <div class="payment-info">
            <div class="info-row">订单编号: {{ item.orderNumber }}</div>
            <div class="info-row">
              剩余支付时间: 
              <span :class="item.isExpired ? 'expired' : 'active'">
                {{ item.isExpired ? '已过期' : item.remainingTime }}
              </span>
            </div>
            <div class="info-row">金额: {{ item.amount }}</div>
            <div class="info-row">支付方式: {{ item.paymentMethod }}</div>
          </div>
          <div class="payment-actions">
            <button class="btn btn-green" @click="updatePayment(index)">更新信息</button>
            <button class="btn btn-purple" @click="copyCard(item)">复制卡片</button>
            <button class="btn btn-purple" @click="copyLink(item)">复制链接</button>
          </div>
        </div>
      </div>

      <!-- 添加/编辑弹窗 -->
      <div class="modal-overlay" v-if="showAddModal">
        <div class="modal-sheet">
          <div class="sheet-header">
            <span class="sheet-title">{{ editIndex >= 0 ? '编辑付款页' : '添加新的付款页' }}</span>
            <span class="sheet-close" @click="showAddModal = false">关闭</span>
          </div>
          <div class="sheet-body">
            <div class="form-row">
              <label>付款金额</label>
              <input type="text" v-model="form.amount" placeholder="请输入付款金额">
            </div>
            <div class="form-row">
              <label>接口</label>
              <input type="text" v-model="form.apiLink" placeholder="请输入接口链接">
            </div>
            <div class="form-row">
              <label>过期/分</label>
              <input type="number" v-model="form.validMinutes" placeholder="请输入过期时间/分钟">
            </div>
            <div class="form-row">
              <label>支付方式</label>
              <select v-model="form.paymentMethod">
                <option value="支付宝">支付宝</option>
                <option value="微信">微信</option>
              </select>
            </div>
          </div>
          <div class="sheet-footer">
            <button class="create-btn" @click="savePayment">创建</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { apiFetch } from '@/config/api'

export default {
  name: 'PaymentPage',
  emits: ['close', 'copy-success'],
  setup(props, { emit }) {
    const showAddModal = ref(false)
    const editIndex = ref(-1)
    const paymentList = ref([])
    let timer = null

    const form = reactive({
      orderNumber: '',
      amount: '',
      apiLink: '',
      paymentMethod: '支付宝',
      validMinutes: 30
    })

    // 生成随机订单号
    const generateOrderNumber = () => {
      return Math.floor(Math.random() * 9000000000000000) + 1000000000000000
    }

    // 计算剩余时间
    const calculateRemainingTime = (expireTime) => {
      const now = Date.now()
      const diff = expireTime - now
      if (diff <= 0) return { isExpired: true, remainingTime: '已过期' }
      
      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      return {
        isExpired: false,
        remainingTime: `${minutes}分${seconds}秒`
      }
    }

    // 更新所有付款页的剩余时间
    const updateRemainingTimes = () => {
      paymentList.value = paymentList.value.map(item => {
        const { isExpired, remainingTime } = calculateRemainingTime(item.expireTime)
        return { ...item, isExpired, remainingTime }
      })
    }

    // 保存付款页
    const savePayment = async () => {
      const expireTime = Date.now() + form.validMinutes * 60000
      const { isExpired, remainingTime } = calculateRemainingTime(expireTime)
      
      const newItem = {
        orderNumber: form.orderNumber || generateOrderNumber(),
        amount: form.amount,
        apiLink: form.apiLink,
        paymentMethod: form.paymentMethod,
        expireTime,
        isExpired,
        remainingTime
      }

      // 保存到后端
      try {
        if (editIndex.value >= 0) {
          await apiFetch(`/api/payment/update/${newItem.orderNumber}`, {
            method: 'PUT',
            body: JSON.stringify(newItem)
          })
          paymentList.value[editIndex.value] = newItem
        } else {
          await apiFetch('/api/payment/create', {
            method: 'POST',
            body: JSON.stringify(newItem)
          })
          paymentList.value.push(newItem)
        }
      } catch (e) {
        console.error('保存到后端失败', e)
        // 即使后端失败也保存到本地
        if (editIndex.value >= 0) {
          paymentList.value[editIndex.value] = newItem
        } else {
          paymentList.value.push(newItem)
        }
      }

      // 保存到localStorage
      localStorage.setItem('paymentList', JSON.stringify(paymentList.value))
      
      // 重置表单
      resetForm()
      showAddModal.value = false
    }

    // 重置表单
    const resetForm = () => {
      form.orderNumber = ''
      form.amount = ''
      form.apiLink = ''
      form.paymentMethod = '支付宝'
      form.validMinutes = 30
      editIndex.value = -1
    }

    // 更新付款信息
    const updatePayment = (index) => {
      const item = paymentList.value[index]
      form.orderNumber = item.orderNumber
      form.amount = item.amount
      form.apiLink = item.apiLink || ''
      form.paymentMethod = item.paymentMethod
      form.validMinutes = 30
      editIndex.value = index
      showAddModal.value = true
    }

    // 兼容的复制方法
    const copyToClipboard = (text) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          emit('copy-success')
        }).catch(() => {
          fallbackCopy(text)
        })
      } else {
        fallbackCopy(text)
      }
    }

    const fallbackCopy = (text) => {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        emit('copy-success')
      } catch (e) {
        alert('复制失败，请手动复制')
      }
      document.body.removeChild(textarea)
    }

    // 复制卡片
    const copyCard = (item) => {
      const cardText = `#payid${item.orderNumber}#`
      copyToClipboard(cardText)
    }

    // 复制链接
    const copyLink = (item) => {
      const link = `${window.location.origin}/payment?order=${item.orderNumber}`
      copyToClipboard(link)
    }

    // 返回
    const goBack = () => {
      emit('close')
    }

    // 从后端加载数据
    const loadFromBackend = async () => {
      try {
        const res = await apiFetch('/api/payment/list')
        if (res.code === 0 && res.data) {
          paymentList.value = res.data.map(item => {
            const { isExpired, remainingTime } = calculateRemainingTime(item.expireTime)
            return { ...item, isExpired, remainingTime }
          })
          localStorage.setItem('paymentList', JSON.stringify(paymentList.value))
        }
      } catch (e) {
        console.error('从后端加载失败', e)
        // 从localStorage加载
        const saved = localStorage.getItem('paymentList')
        if (saved) {
          paymentList.value = JSON.parse(saved)
          updateRemainingTimes()
        }
      }
    }

    onMounted(() => {
      // 先从后端加载，失败则从localStorage加载
      loadFromBackend()
      
      // 每秒更新剩余时间
      timer = setInterval(updateRemainingTimes, 1000)
    })

    onUnmounted(() => {
      if (timer) clearInterval(timer)
    })

    return {
      showAddModal,
      editIndex,
      paymentList,
      form,
      savePayment,
      updatePayment,
      copyCard,
      copyLink,
      goBack
    }
  }
}
</script>

<style scoped>
.payment-page {
  height: 100%;
  width: 100%;
  background: #fff;
  overflow: hidden;
}

.main-container {
  height: 100%;
  width: 100%;
  background: #fff;
  overflow-y: auto;
  overscroll-behavior: none;
}

/* 头部 */
.header {
  background: #fff;
  padding-left: 16px;
  align-items: center;
  display: flex;
  height: 50px;
  border-bottom: 1px solid #f0f0f0;
}

.header svg {
  cursor: pointer;
}

.header-title {
  margin-left: 12px;
  font-size: 16px;
}

/* 添加按钮 */
.add-btn {
  margin: 16px;
  background: #4a7dff;
  color: #fff;
  border-radius: 8px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-size: 15px;
}

.add-icon {
  font-size: 18px;
}

/* 付款页列表 */
.payment-list {
  padding: 0 16px;
}

.payment-item {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}

.payment-info {
  margin-bottom: 12px;
}

.info-row {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
  line-height: 1.5;
}

.expired {
  color: #e74c3c;
  font-weight: 500;
}

.active {
  color: #27ae60;
  font-weight: 500;
}

/* 操作按钮 */
.payment-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 10px 16px;
  border-radius: 6px;
  border: none;
  font-size: 13px;
  cursor: pointer;
  color: #fff;
}

.btn-green {
  background: #27ae60;
}

.btn-purple {
  background: #9b59b6;
}

.btn-cancel {
  background: #95a5a6;
}

.btn-confirm {
  background: #4a7dff;
}

/* 底部弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1000;
}

.modal-sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.sheet-header {
  padding: 18px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-bottom: 1px solid #f0f0f0;
}

.sheet-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.sheet-close {
  position: absolute;
  right: 16px;
  font-size: 15px;
  color: #4a7dff;
  cursor: pointer;
}

.sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.form-row {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.form-row label {
  width: 70px;
  font-size: 15px;
  color: #333;
  flex-shrink: 0;
}

.form-row input,
.form-row select {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  color: #333;
  background: transparent;
}

.form-row input::placeholder {
  color: #999;
}

.sheet-footer {
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

.create-btn {
  width: 100%;
  padding: 14px;
  background: #4a7dff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
}
</style>
